import is from '@sindresorhus/is';
import { Fragment, type ReactNode } from 'react';

import type {
    AgentChatMessage,
    AssistantContextRefreshEntry,
    AssistantEventEntry,
    AwaitingCallback,
    ChatBlock,
    ChatBlockType,
    StatusUpdateBlock,
} from '../../data-contract/chat';
import { activityTimelineBlockRenderer } from './activity-timeline';
import { compactStatusUpdates, type FlattenedBlock, mergeActivityTimelineSteps } from './activity-timeline-compaction';
import { changeProposalBlockRenderer } from './change-proposal';
import { clarifyOptionsBlockRenderer } from './clarify-options';
import { ContextRefreshSideEffect } from './context-refresh';
import { questionnaireBlockRenderer } from './questionnaire';
import { statusUpdateBlockRenderer } from './status-update';
import { textBlockRenderer } from './text-block';
import type { BlockRenderContext, BlockRenderer, BlockStatus, InputOverrideRenderResult } from './types';

export { ContextRefreshSideEffect } from './context-refresh';
export type { BlockRenderContext, BlockRenderer, BlockStatus, InputOverrideRenderResult } from './types';

type BlockRendererRegistry = {
    readonly [K in ChatBlockType]: BlockRenderer<Extract<ChatBlock, { type: K }>>;
};

const RENDERERS = {
    'activity-timeline': activityTimelineBlockRenderer,
    'change-proposal': changeProposalBlockRenderer,
    'clarify-options': clarifyOptionsBlockRenderer,
    questionnaire: questionnaireBlockRenderer,
    'status-update': statusUpdateBlockRenderer,
    text: textBlockRenderer,
} as const satisfies BlockRendererRegistry;

type RendererFor<TBlock extends ChatBlock> = BlockRenderer<Extract<ChatBlock, { type: TBlock['type'] }>>;

const findRendererFor = <TBlock extends ChatBlock>(block: TBlock): RendererFor<TBlock> =>
    RENDERERS[block.type] as RendererFor<TBlock>;

const isAnsweredBlock = (block: ChatBlock): boolean => findRendererFor(block).isAnswered(block);

const rendersInlineContent = (block: ChatBlock): boolean => {
    const renderer = findRendererFor(block);
    if (is.undefined(renderer.renderInline)) return false;
    return renderer.rendersInlineContent?.(block) ?? true;
};

const isInputOverrideRenderResult = (
    override: ReactNode | InputOverrideRenderResult,
): override is InputOverrideRenderResult =>
    is.object(override) && 'node' in override && 'suppressInputChrome' in override;

export type BlockWithMeta = {
    readonly block: ChatBlock;
    readonly eventId: string;
    readonly status: BlockStatus;
};

const collectEventEntries = (message: AgentChatMessage): readonly AssistantEventEntry[] =>
    (message.entries ?? []).filter((entry): entry is AssistantEventEntry => entry.type === 'event');

const mergeBlocks = (previous: ChatBlock | undefined, next: ChatBlock): ChatBlock => {
    if (previous?.type === 'activity-timeline' && next.type === 'activity-timeline') {
        return mergeActivityTimelineSteps(previous, next);
    }

    return next;
};

const getFlattenedBlockKey = (block: ChatBlock, eventId: string): string => {
    if (block.type === 'status-update') return `${eventId}:${block.blockId}`;
    return block.blockId;
};

const flattenBlocks = (message: AgentChatMessage): readonly FlattenedBlock[] => {
    // History can contain a Review and a Result envelope for the same `blockId` (HITL flow).
    // Keep the latest occurrence per blockId so the answered Result wins over the open Review —
    // otherwise the stale Review re-renders its callback buttons after the user already decided.
    const lastByBlockId = new Map<string, FlattenedBlock>();
    for (const entry of collectEventEntries(message)) {
        for (const block of entry.envelope.payload.blocks) {
            const key = getFlattenedBlockKey(block, entry.envelope.eventId);
            const previous = lastByBlockId.get(key);
            lastByBlockId.set(key, {
                block: mergeBlocks(previous?.block, block),
                eventId: entry.envelope.eventId,
                timestamp: entry.envelope.timestamp,
            });
        }
    }
    return compactStatusUpdates([...lastByBlockId.values()], rendersInlineContent);
};

const resolveActiveBlockId = (
    flat: readonly { readonly block: ChatBlock; readonly eventId: string }[],
    isLatestAssistant: boolean,
    awaitingCallback: AwaitingCallback | null,
): string | null => {
    if (!isLatestAssistant) return null;
    if (!is.null(awaitingCallback)) {
        const match = flat.find((item) => item.block.blockId === awaitingCallback.blockId);
        if (!is.undefined(match) && !isAnsweredBlock(match.block)) return awaitingCallback.blockId;
    }
    const last = flat[flat.length - 1];
    if (is.undefined(last) || isAnsweredBlock(last.block)) return null;
    return last.block.blockId;
};

export const computeMessageBlocksMeta = (
    message: AgentChatMessage,
    isLatestAssistant: boolean,
    awaitingCallback: AwaitingCallback | null,
): readonly BlockWithMeta[] => {
    const flat = flattenBlocks(message);
    const activeBlockId = resolveActiveBlockId(flat, isLatestAssistant, awaitingCallback);
    return flat.map((item) => {
        const isActive =
            !is.null(activeBlockId) && item.block.blockId === activeBlockId && !isAnsweredBlock(item.block);
        const status: BlockStatus = isActive ? 'active' : 'completed';
        return { ...item, status };
    });
};

export const findInputOverrideForMessage = (
    message: AgentChatMessage,
    awaitingCallback: AwaitingCallback | null,
): InputOverrideRenderResult | null => {
    const blocks = computeMessageBlocksMeta(message, true, awaitingCallback);
    const active = blocks.find((item) => item.status === 'active');
    if (is.undefined(active)) return null;
    const renderer = findRendererFor(active.block);
    if (is.undefined(renderer.renderInputOverride)) return null;
    const context: BlockRenderContext = {
        eventId: active.eventId,
        interactive: true,
        isTurnComplete: message.status !== 'pending',
        status: 'active',
    };
    const override = renderer.renderInputOverride(active.block, context);
    if (is.nullOrUndefined(override)) return null;
    if (isInputOverrideRenderResult(override)) return override;
    return { node: override, suppressInputChrome: false };
};

// Retained for legacy callers that still need the latest raw status update.
// The current chat flow renders status updates inline through the compacted activity timeline.
export const findLatestStatusUpdate = (message: AgentChatMessage): StatusUpdateBlock | null => {
    const entries = collectEventEntries(message);
    for (let entryIndex = entries.length - 1; entryIndex >= 0; entryIndex--) {
        const blocks = entries[entryIndex]?.envelope.payload.blocks ?? [];
        for (let blockIndex = blocks.length - 1; blockIndex >= 0; blockIndex--) {
            const block = blocks[blockIndex];
            if (block?.type === 'status-update') return block;
        }
    }
    return null;
};

export const hasActivityTimeline = (message: AgentChatMessage): boolean =>
    flattenBlocks(message).some((item) => item.block.type === 'activity-timeline');

export const renderContextRefreshEntry = (entry: AssistantContextRefreshEntry): ReactNode => (
    <ContextRefreshSideEffect key={entry.envelope.eventId} envelope={entry.envelope} />
);

export const renderBlocksForEvent = (
    eventId: string,
    blocksMeta: readonly BlockWithMeta[],
    isTurnComplete: boolean,
): ReactNode => {
    const eventBlocks = blocksMeta.filter((item) => item.eventId === eventId);
    return (
        <>
            {eventBlocks.map(({ block, status }) => {
                const renderer = findRendererFor(block);
                if (is.undefined(renderer.renderInline)) return null;
                const context: BlockRenderContext = {
                    eventId,
                    interactive: status === 'active',
                    isTurnComplete,
                    status,
                };
                const node = renderer.renderInline(block, context);
                if (is.nullOrUndefined(node)) return null;
                return <Fragment key={block.blockId}>{node}</Fragment>;
            })}
        </>
    );
};
